// =============================================================================
// usableAbilityFormulas.js — shared pure-formula helpers for usable-ability modals
// =============================================================================
// [CREATED: 2026-05-30 — Sub-plan A2-classres-consumers]
// [PURPOSE]: Rule 22 extraction — pure formulas shared between ClassResourcesPanel.js
//   (production call sites) and src/__tests__/usableAbilityModal/a2-consumers.test.js.
//   Both import from here; the test never defines inline replicas.
//
// IMPORTANT: These functions are pure (no imports, no side effects).
//   The MODIFIER passed to computeDc / computeKiSaveDc MUST come from
//   effectCalculator.getEffectiveModifier() at the call site, NOT recomputed
//   inline, so that item bonuses (Headband of Intellect, Periapt of Wisdom)
//   and feat ASIs are included (Rule 2 — effectCalculator for ALL stat calcs).

/**
 * Scale Breath Weapon dice by character level (PHB p.34).
 * Die table: 2d6 (L1-5) | 3d6 (L6-10) | 4d6 (L11-15) | 5d6 (L16+).
 *
 * @param {number} level - Character level (1-20).
 * @returns {string} Dice string e.g. '2d6'.
 */
export function scaleBreathWeaponDice(level) {
  if (level >= 16) return '5d6';
  if (level >= 11) return '4d6';
  if (level >= 6) return '3d6';
  return '2d6';
}

/**
 * Compute a spell/ability save DC from a pre-computed ability modifier.
 * Formula: 8 + proficiency bonus + ability modifier (PHB p.203).
 *
 * The modifier MUST be supplied by effectCalculator.getEffectiveModifier() at
 * the call site — never computed inline — so magic items and feat ASIs apply.
 *
 * @param {number} mod - Pre-computed ability modifier (e.g. from effectCalculator).
 * @param {number} profBonus - Proficiency bonus.
 * @returns {number} Save DC.
 */
export function computeAbilitySaveDc(mod, profBonus) {
  return 8 + mod + profBonus;
}

/**
 * Infer the saving throw ability from a Dragonborn breath weapon shape string.
 * Line shapes (Dex. save) → 'dexterity'. Cone shapes (Con. save) → 'constitution'.
 *
 * @param {string|null} shape - Breath weapon shape descriptor from DRAGONBORN_ANCESTRY.
 * @returns {'dexterity'|'constitution'} Saving throw ability name.
 */
export function inferBreathWeaponSave(shape) {
  return (shape || '').includes('Con.') ? 'constitution' : 'dexterity';
}

/**
 * Compute damage after a saving throw with half-on-save semantics.
 *
 * @param {number} rolled - Total damage rolled.
 * @param {'success'|'failure'} outcome - Result of the saving throw.
 * @returns {number} Actual damage dealt.
 */
export function applyHalfOnSave(rolled, outcome) {
  return outcome === 'success' ? Math.floor(rolled / 2) : rolled;
}
